import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CategoryRail from './CategoryRail';
import FeaturedHeroCarousel from './FeaturedHeroCarousel';
import Header from './common/Header';
import Footer from './common/Footer';
import LoadingSpinner from './common/LoadingSpinner';
import { HomepageRailsSkeleton } from './common/SkeletonLoaders';
import ErrorBoundary from './common/ErrorBoundary';
import PDPModal from './PDPModal';
import DynamicMetaTags from './SEO/DynamicMetaTags';
import LoginCTASection from './PlaceholderRails/LoginCTASection';
import { useAppLoad } from '../contexts/AppLoadContext';
import { useNavigation } from '../hooks/useNavigation';
import { useContinueWatching } from '../hooks/useContinueWatching';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { userDataService } from '../services/UserDataService';
import spotlightService from '../services/SpotlightService';
import './Homepage.css';

// Debug component (dev-only)
const DebugInfo = ({ categories, featuredCategory }) => {
  if (process.env.NODE_ENV !== 'development') return null;

  return (
    <div className="p-4 mb-6 bg-gray-800 rounded">
      <h3 className="mb-2 font-bold text-white">Debug Information:</h3>
      <div className="space-y-1 text-sm text-white/70">
        <p>Categories loaded: {categories.length}</p>
        <p>Featured category: {featuredCategory?.name || 'None'}</p>
        <p>
          Movies category:{' '}
          {categories.some(cat => cat._id === '642af6951636564187513f10') ? 'Yes' : 'No'}
        </p>
      </div>
    </div>
  );
};

// Error fallback for categories
const CategoriesError = ({ error, retry }) => (
  <div className="flex flex-col items-center justify-center min-h-screen p-6">
    <div className="max-w-md text-center">
      <h2 className="mb-4 text-2xl font-bold text-white">Unable to Load Content</h2>
      <p className="mb-6 text-white/70">
        We're having trouble loading the streaming content. Please check your connection and try
        again.
      </p>
      <button
        onClick={retry}
        className="px-6 py-3 font-medium text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
      >
        Try Again
      </button>
    </div>
  </div>
);

const Homepage = () => {
  // Use custom hooks for cleaner logic
  const { categories, loading, error, retry } = useAppLoad();
  const { handleContentSelect, handleCategorySelect } = useNavigation();
  const {
    continueWatchingData,
    hasContinueWatching,
    loading: continueWatchingLoading,
  } = useContinueWatching();
  const { isAuthenticated } = useStreamAuth();
  const location = useLocation();

  // Resumption data for ThumbnailHoverModal
  const [resumptionData, setResumptionData] = useState({
    continueWatching: [],
    watchAgain: []
  });

  // Fetch resumption data for ThumbnailHoverModal
  useEffect(() => {
    const fetchResumptionData = async () => {
      if (!isAuthenticated) {
        setResumptionData({ continueWatching: [], watchAgain: [] });
        return;
      }

      try {
        const data = await userDataService.refreshResumptionsData();
        setResumptionData({
          continueWatching: data?.['continue-watching'] || [],
          watchAgain: data?.['watch-again'] || []
        });
      } catch (error) {
        console.error('Homepage: Error fetching resumption data:', error);
        setResumptionData({ continueWatching: [], watchAgain: [] });
      }
    };

    fetchResumptionData();
  }, [isAuthenticated]);

  // Debug logging for continue watching

  // PDP Modal state
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showPDPModal, setShowPDPModal] = useState(false);
  const [hasProcessedPermalink, setHasProcessedPermalink] = useState(false);

  // Check for channel query parameter on load and when categories change
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const channelSlug = searchParams.get('channel');

    if (!channelSlug || hasProcessedPermalink) return;

    // Check if channel data was passed via location.state (e.g., after purchase)
    const channelFromState = location.state?.channel;

    if (channelFromState && (
      channelFromState.slug === channelSlug ||
      channelFromState._id === channelSlug ||
      channelFromState.permalink === channelSlug
    )) {
      console.log('🔄 Homepage: Using channel from navigation state with updated subscription:', channelFromState);
      setSelectedChannel(channelFromState);
      setShowPDPModal(true);
      setHasProcessedPermalink(true);
      return;
    }

    // Find the channel across all categories AND continue watching data
    let foundChannel = null;

    // First, check Continue Watching data
    if (continueWatchingData && continueWatchingData.length > 0) {
      foundChannel = continueWatchingData.find(
        channel =>
          channel.slug === channelSlug ||
          channel._id === channelSlug ||
          channel.contentId === channelSlug ||
          channel.dspro_id === channelSlug ||
          channel.permalink === channelSlug,
      );
    }

    // Then search in categories
    if (!foundChannel && categories.length > 0) {
      for (const category of categories) {
        if (category.channels && Array.isArray(category.channels)) {
          foundChannel = category.channels.find(
            channel =>
              channel.slug === channelSlug ||
              channel._id === channelSlug ||
              channel.permalink === channelSlug,
          );
          if (foundChannel) break;
        }
      }
    }

    if (foundChannel) {
      setSelectedChannel(foundChannel);
      setShowPDPModal(true);
      setHasProcessedPermalink(true);
      return;
    }

    // Not found locally — fetch directly by slug from API
    setHasProcessedPermalink(true);
    const openPDP = (response) => {
      const channel = response?.channels?.[0] || response;
      if (channel && (channel.slug || channel._id)) {
        setSelectedChannel(channel);
        setShowPDPModal(true);
      }
    };
    spotlightService.getChannelBySlug(channelSlug)
      .then(openPDP)
      .catch(() => {
        spotlightService.getChannelBySlugPublic(channelSlug)
          .then(openPDP)
          .catch(() => {});
      });
  }, [location.search, location.state, categories, continueWatchingData, hasProcessedPermalink]);

  // Handle PDP modal close
  const handlePDPModalClose = () => {
    setShowPDPModal(false);
    setSelectedChannel(null);

    // Clear the URL parameter to prevent reopening
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('channel')) {
      searchParams.delete('channel');
      const newUrl = `${window.location.pathname}${
        searchParams.toString() ? '?' + searchParams.toString() : ''
      }`;
      window.history.replaceState({}, '', newUrl);
    }
  };

  // Memoized navigation handlers
  const onMovieSelect = useCallback(
    movieId => {
      handleContentSelect(movieId, 'movie');
    },
    [handleContentSelect],
  );

  const onSeriesSelect = useCallback(
    seriesId => {
      handleContentSelect(seriesId, 'series');
    },
    [handleContentSelect],
  );

  const onCategoryClick = useCallback(
    categoryId => {
      handleCategorySelect(categoryId);
    },
    [handleCategorySelect],
  );

  // Use the first category as the featured carousel content
  // The first category in the API response becomes the hero carousel
  const featuredCategory = categories.length > 0 ? categories[0] : null;
  // Regular categories are everything except the first one
  const regularCategories = categories.length > 1 ? categories.slice(1) : [];
  // Loading state
  if (loading || (!featuredCategory && !regularCategories?.length)) {
    return <LoadingSpinner message="Loading streaming content..." />;
  }

  // Error state
  if (error) {
    return <CategoriesError error={error} retry={retry} />;
  }

  return (
    <div className="min-h-screen bg-page homepage">
      {/* Dynamic Meta Tags for Social Sharing and SEO */}
      <DynamicMetaTags />
      
      <Header />

      {/* Full-screen Hero Carousel - Outside container for full width */}
      <ErrorBoundary fallback="Failed to load featured content">
        <Suspense fallback={<LoadingSpinner message="Loading featured content..." />}>
          {featuredCategory && (
            <FeaturedHeroCarousel
              category={featuredCategory}
              onMovieSelect={onMovieSelect}
              onSeriesSelect={onSeriesSelect}
            />
          )}
        </Suspense>
      </ErrorBoundary>

      {/* Content Rails - With overlapping effect */}
      <main className="container relative mx-auto">
        {/* Debug Info - Commented out for production */}
        {/* <DebugInfo categories={categories} featuredCategory={featuredCategory} /> */}

        <ErrorBoundary fallback="Failed to load categories">
          <Suspense fallback={<HomepageRailsSkeleton railCount={3} />}>
            {regularCategories.length > 0 ? (
              regularCategories.map((category, idx) => {
                // Insert Continue Watching rail as the third rail (after index 1) for authenticated users
                // OR insert CTA sections for non-authenticated users
                if (idx === 2) {
                  return (
                    <React.Fragment key={`continue-watching-${category._id}`}>
                      {/* Continue Watching Rail for Authenticated Users */}
                      {isAuthenticated && hasContinueWatching && (
                        <div className="mb-8">
                          <CategoryRail
                            category={{
                              _id: 'continue-watching',
                              name: 'Continue Watching',
                              display_name: 'Continue Watching',
                              channels: continueWatchingData,
                              style: 'continue-watching',
                            }}
                            preCalculatedContinueWatching={resumptionData.continueWatching}
                            preCalculatedWatchAgain={resumptionData.watchAgain}
                            onCategorySelect={onCategoryClick}
                            onMovieSelect={onMovieSelect}
                            onSeriesSelect={onSeriesSelect}
                          />
                        </div>
                      )}

                      {/* Continue Watching CTA for Non-Authenticated Users */}
                      {!isAuthenticated && (
                        <LoginCTASection
                          sectionTitle="Continue Watching"
                          title="Pick up right where you left off."
                          subtitle="Login now to continue watching your favorite series & movies."
                        />
                      )}

                      {/* Regular Category Rail */}
                      <div
                        className={idx === 0 ? '-mt-72 md:-mt-80 xl:-mt-80 z-20 relative' : 'mb-8'}
                      >
                        <CategoryRail
                          category={category}
                          preCalculatedContinueWatching={resumptionData.continueWatching}
                          preCalculatedWatchAgain={resumptionData.watchAgain}
                          onCategorySelect={onCategoryClick}
                          onMovieSelect={onMovieSelect}
                          onSeriesSelect={onSeriesSelect}
                        />
                      </div>
                    </React.Fragment>
                  );
                }

                // Insert Watch Again CTA for non-authenticated users at index 4
                if (idx === 4 && !isAuthenticated) {
                  return (
                    <React.Fragment key={`watch-again-${category._id}`}>
                      {/* Watch Again CTA for Non-Authenticated Users */}
                      <LoginCTASection
                        sectionTitle="Watch Again"
                        title="Rediscover your favorites."
                        subtitle="Login now to watch again your favorite series & movies."
                      />

                      {/* Regular Category Rail */}
                      <div className="mb-8">
                        <CategoryRail
                          category={category}
                          preCalculatedContinueWatching={resumptionData.continueWatching}
                          preCalculatedWatchAgain={resumptionData.watchAgain}
                          onCategorySelect={onCategoryClick}
                          onMovieSelect={onMovieSelect}
                          onSeriesSelect={onSeriesSelect}
                        />
                      </div>
                    </React.Fragment>
                  );
                }

                // Regular category rail
                return (
                  <div
                    key={category._id}
                    className={idx === 0 ? '-mt-72 md:-mt-80 xl:-mt-80 z-20 relative' : 'mb-8'}
                  >
                    <CategoryRail
                      category={category}
                      preCalculatedContinueWatching={resumptionData.continueWatching}
                      preCalculatedWatchAgain={resumptionData.watchAgain}
                      onCategorySelect={onCategoryClick}
                      onMovieSelect={onMovieSelect}
                      onSeriesSelect={onSeriesSelect}
                    />
                  </div>
                );
              })
            ) : (
              <HomepageRailsSkeleton railCount={5} />
            )}
          </Suspense>
        </ErrorBoundary>
      </main>

      <Footer />

      {/* PDP Modal for direct channel links */}
      {selectedChannel && (
        <PDPModal
          isVisible={showPDPModal}
          channel={selectedChannel}
          onClose={handlePDPModalClose}
        />
      )}

      {/* Meta Tag Tester - Development Only */}
    </div>
  );
};

export default Homepage;
